import styled from '@emotion/styled';
import { faBars, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { FC } from 'react';
import { SortableHandle } from 'react-sortable-hoc';
import { Project } from '../../Api';
import { Button } from '../shared/Button';
import theme from '../shared/theme';

type Props = {
  project: Project;
  addDemo(projectId: number): void;
  cancelDemo(projectId: number): void;
};

export const DemoSlotRow: FC<Props> = ({ project, addDemo, cancelDemo }) => {
  const isDemoScheduled = project.demoSlot !== undefined;
  return (
    <Row isScheduled={isDemoScheduled}>
      <DragHandleColumn>
        {isDemoScheduled && (
          <DragHandle>
            <DragIcon icon={faBars} />
          </DragHandle>
        )}
      </DragHandleColumn>
      <SlotColumn isScheduled={isDemoScheduled}>
        {isDemoScheduled ? (
          <SlotBadge>{project.demoSlot! + 1}</SlotBadge>
        ) : (
          <EmptySlot>-</EmptySlot>
        )}
      </SlotColumn>
      <NameColumn>{project.name}</NameColumn>
      <ButtonColumn>
        {isDemoScheduled && (
          <ActionButton size="sm" category="danger" onClick={() => cancelDemo(project.projectId)} title="Cancel Demo">
            <FontAwesomeIcon icon={faTrash} />
          </ActionButton>
        )}
        {isDemoScheduled || (
          <ActionButton size="sm" category="success" onClick={() => addDemo(project.projectId)} title="Schedule Demo">
            <FontAwesomeIcon icon={faPlus} />
          </ActionButton>
        )}
      </ButtonColumn>
    </Row>
  );
};

const Row = styled.tr<{ isScheduled: boolean }>`
  background: ${props => (props.isScheduled ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0.5)')};
  transition: all 0.2s ease;
  border-left: 3px solid ${props => (props.isScheduled ? theme.colors.green : 'transparent')};
  
  &.sorting button {
    display: none;
  }
`;

const DragHandleColumn = styled.td`
  width: 40px;
  text-align: center;
  
  @media (max-width: 768px) {
    width: 30px;
  }
`;

const DragHandleTarget = styled.div`
  padding: 8px;
  margin: -8px;
  background: transparent;
  cursor: grab;
  
  &:active {
    cursor: grabbing;
  }
`;

const DragHandle = SortableHandle(DragHandleTarget);

const DragIcon = styled(FontAwesomeIcon)`
  transform: scale(1.2);
  color: ${theme.colors.lightGrey};
  
  &:hover {
    color: ${theme.colors.blue};
  }
`;

const SlotColumn = styled.td<{ isScheduled: boolean }>`
  width: 60px;
  text-align: center;
  font-weight: ${props => (props.isScheduled ? '600' : 'normal')};
  
  @media (max-width: 768px) {
    width: 50px;
  }
`;

const SlotBadge = styled.div`
  display: inline-block;
  background: ${theme.colors.green};
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
`;

const EmptySlot = styled.div`
  color: ${theme.colors.lightGrey};
`;

const NameColumn = styled.td`
  font-weight: 500;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const ButtonColumn = styled.td`
  width: 60px;
  text-align: center;
  padding-right: 0 !important;
  padding-left: 0 !important;
  
  @media (max-width: 768px) {
    width: 50px;
  }
`;

const ActionButton = styled(Button)`
  min-width: 40px;
  
  @media (max-width: 768px) {
    min-width: 35px;
    padding: 6px 8px;
  }
`;
